import React, { useState, useEffect, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  FiVideo, 
  FiX, 
  FiCheck,
  FiSearch,
  FiUpload,
  FiPlay,
  FiRefreshCw
} from 'react-icons/fi'
import { useMedia } from '../../hooks/useMedia'
import { MediaFile } from '../../types/media'
import VideoUpload from './VideoUpload'

interface VideoPickerProps {
  isOpen: boolean
  onClose: () => void
  onSelect: (videoUrl: string) => void
  selectedUrl?: string
}

const VideoPicker: React.FC<VideoPickerProps> = ({
  isOpen,
  onClose,
  onSelect,
  selectedUrl
}) => {
  const { mediaFiles, loading, syncWithStorage } = useMedia()
  const [searchQuery, setSearchQuery] = useState('')
  const [showUpload, setShowUpload] = useState(false)
  const [syncing, setSyncing] = useState(false)
  const modalRef = useRef<HTMLDivElement>(null)

  // Lock body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      // Save current scroll position
      const scrollY = window.scrollY
      document.body.style.position = 'fixed'
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = '100%'
      
      // Focus the modal
      modalRef.current?.focus()
    } else {
      // Restore scroll position
      const scrollY = document.body.style.top
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
      window.scrollTo(0, parseInt(scrollY || '0') * -1)
    }

    return () => {
      // Cleanup on unmount
      document.body.style.position = ''
      document.body.style.top = ''
      document.body.style.width = ''
    }
  }, [isOpen])

  const videoFiles = mediaFiles
    .filter(file => file.type === 'video')
    .filter(file => 
      file.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.originalName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      file.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    )

  const handleSelect = (file: MediaFile) => {
    onSelect(file.url)
    onClose()
  }

  const handleUploadComplete = (videoUrl: string) => {
    onSelect(videoUrl)
    setShowUpload(false)
    onClose()
  }

  const handleSync = async () => {
    setSyncing(true)
    await syncWithStorage()
    setSyncing(false)
  }

  const formatDuration = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = Math.floor(seconds % 60)
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 overflow-y-auto"
        onClick={onClose}
      >
        <motion.div
          ref={modalRef}
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden flex flex-col"
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="video-picker-title"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-neutral-200 flex-shrink-0">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-primary/10 rounded-lg">
                <FiVideo className="text-primary" size={20} />
              </div>
              <div>
                <h2 id="video-picker-title" className="text-xl font-bold text-neutral-800">Select Background Video</h2>
                <p className="text-sm text-neutral-600">Choose from uploaded videos or upload new ones</p>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {/* Upload Toggle */}
              <button
                onClick={() => setShowUpload(!showUpload)}
                className={`flex items-center space-x-2 px-4 py-2 rounded-xl transition-all duration-200 ${
                  showUpload
                    ? 'bg-primary text-white'
                    : 'bg-neutral-100 text-neutral-700 hover:bg-neutral-200'
                }`}
              >
                <FiUpload size={16} />
                <span>{showUpload ? 'Hide Upload' : 'Upload New'}</span>
              </button>

              {/* Close Button */}
              <button
                onClick={onClose}
                className="p-2 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded-xl transition-colors"
              >
                <FiX size={20} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0">
            {showUpload ? (
              /* Upload Section */
              <div className="p-6 h-full overflow-y-auto">
                <VideoUpload
                  onUploadComplete={handleUploadComplete}
                  maxFiles={1}
                />
              </div>
            ) : (
              /* Video Library */
              <div className="h-full flex flex-col">
                {/* Search */}
                <div className="p-6 border-b border-neutral-200 flex-shrink-0">
                  <div className="relative">
                    <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400" size={20} />
                    <input
                      type="text"
                      placeholder="Search videos..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-3 border border-neutral-300 rounded-xl focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                {/* Video Grid */}
                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <div className="loading-spinner"></div>
                      <span className="ml-2 text-neutral-600">Loading videos...</span>
                    </div>
                  ) : videoFiles.length === 0 ? (
                    <div className="text-center py-12">
                      <FiVideo size={48} className="text-neutral-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-neutral-800 mb-2">
                        {searchQuery ? 'No videos found' : 'No videos uploaded yet'}
                      </h3>
                      <p className="text-neutral-600 mb-6">
                        {searchQuery 
                          ? 'Try adjusting your search terms'
                          : 'Upload your first background video to get started'
                        }
                      </p>
                      {!searchQuery && (
                        <button
                          onClick={() => setShowUpload(true)}
                          className="btn-primary inline-flex items-center space-x-2"
                        >
                          <FiUpload size={20} />
                          <span>Upload Video</span>
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      {videoFiles.map((file, index) => (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                          onClick={() => handleSelect(file)}
                          className={`group relative bg-neutral-50 rounded-xl overflow-hidden cursor-pointer hover:shadow-lg transition-all duration-200 ${
                            selectedUrl === file.url || selectedUrl === file.optimizedUrl ? 'ring-2 ring-primary' : ''
                          }`}
                        >
                          {/* Selection Indicator */}
                          {(selectedUrl === file.url || selectedUrl === file.optimizedUrl) && (
                            <div className="absolute top-3 right-3 z-10">
                              <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center">
                                <FiCheck className="text-white" size={14} />
                              </div>
                            </div>
                          )}

                          {/* Video Preview */}
                          <div className="aspect-video relative">
                            <video
                              src={file.url}
                              className="w-full h-full object-cover"
                              muted
                              preload="metadata"
                            />
                            
                            {/* Play Overlay */}
                            <div className="absolute inset-0 bg-black bg-opacity-30 flex items-center justify-center group-hover:bg-opacity-20 transition-all duration-200">
                              <div className="w-12 h-12 bg-white bg-opacity-90 rounded-full flex items-center justify-center">
                                <FiPlay className="text-neutral-800 ml-1" size={20} />
                              </div>
                            </div>
                          </div>

                          {/* File Info */}
                          <div className="p-4">
                            <p className="font-medium text-neutral-800 truncate mb-2">
                              {file.originalName}
                            </p>
                            <div className="flex items-center justify-between text-sm text-neutral-600">
                              <span>{formatFileSize(file.size)}</span>
                              {file.duration && (
                                <span>{formatDuration(file.duration)}</span>
                              )}
                              {file.dimensions && (
                                <span>{file.dimensions.width}×{file.dimensions.height}</span>
                              )}
                            </div>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-neutral-200 bg-neutral-50 flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="text-sm text-neutral-600">
                  {videoFiles.length} video{videoFiles.length !== 1 ? 's' : ''} available
                </div>
                
                {/* Sync Button */}
                <button
                  onClick={handleSync}
                  disabled={syncing}
                  className="flex items-center space-x-2 px-3 py-1.5 text-sm bg-neutral-100 text-neutral-700 rounded-lg hover:bg-neutral-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Sync with Storage - removes orphaned records"
                >
                  <FiRefreshCw className={syncing ? 'animate-spin' : ''} size={14} />
                  <span>{syncing ? 'Syncing...' : 'Sync'}</span>
                </button>
              </div>
              
              <div className="flex items-center space-x-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-neutral-600 hover:text-neutral-800 transition-colors"
                >
                  Cancel
                </button>
                
                {selectedUrl && (
                  <button
                    onClick={() => {
                      onSelect(selectedUrl)
                      onClose()
                    }}
                    className="btn-primary flex items-center space-x-2"
                  >
                    <FiCheck size={16} />
                    <span>Use Selected Video</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default VideoPicker
